import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DRIVE_FOLDER_SEARCH_TYPE, GROUPING_VIEW_TYPE } from "@constants";
import {
    createDriveFolder,
    getFoldersList,
    updateFolderState,
    updateSelectedFolder,
    updateFolderInfo,
} from "@modules/folderTreeView";
import { setNotification } from "@modules/notification";
import { useTranslation } from "react-i18next";
import store from "@store/store";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import useAbortController from "@hooks/useAbortController";

const useFolder = ({ domain, driveInfo }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getVirtualFolderFullPathList, getVirtualFolder } = useVirtualFolderPath({ domain, driveInfo });

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);

    const [currentFolderInfo, setCurrentFolderInfo] = useState(null);

    const { getNewController, abort, abortAll } = useAbortController();

    /**
     * 폴더 트리 클릭 처리 — 펼침/접힘과 폴더 선택·목록 이동을 함께 수행한다.
     *
     * @param {Object} item 대상 폴더
     * @param {boolean} isClickArrow 캐럿(펼침 아이콘) 클릭 — 펼침/접힘만 하고 폴더 선택·목록 이동은 하지 않는다
     * @param {boolean} checkUseHistory 앞/뒤 히스토리 재생 — 선택 히스토리를 다시 쌓지 않는다
     * @param {Function} onClickTreeItem 폴더 선택 시 목록·URL을 갱신하는 콜백
     * @param {{ toggleFold?: boolean }} options toggleFold: 열려 있으면 접는다 (트리 행 클릭 전용 — 브레드크럼·히스토리·루트 초기화는 펼치기만 한다)
     */
    const onClickFolderTree = async (
        item,
        isClickArrow,
        checkUseHistory = false,
        onClickTreeItem = () => {},
        options = {},
    ) => {
        try {
            const folderTreeView = store.getState().folderTreeView; //Note: TreeViewLayout Mount 직후 호출 시 driveList, folderList가 갱신되지 않은 상태임
            if (!folderTreeView.driveList.data?.length || !folderTreeView.folderList.data[item.id]) return false;

            const folder = folderTreeView.folderList.data[item.id];
            // 트리 행 클릭도 캐럿과 동일하게 토글한다. 브레드크럼·상위 폴더 버튼·앞뒤 히스토리·루트 초기화도
            // isClickArrow=false로 들어오므로, toggleFold 없이는 기존대로 펼치기만 한다
            const shouldFold = (isClickArrow || options.toggleFold) && !folder.isFold;

            if (!shouldFold) {
                //이미 드레인 중인 폴더면 진행 중인 요청을 유지 (getNewController가 같은 키의 요청을 abort하므로 호출하지 않음)
                if (!folder.pending) {
                    const queryParameters = {
                        parentId: item.id,
                        searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
                    };
                    const headerParams = { projectId: project.id };
                    const signal = getNewController(`folderChildren:${item.id}`);

                    dispatch(
                        getFoldersList({
                            driveId: item.driveId,
                            queryParameters,
                            headerParams,
                            signal,
                        }),
                    ); //TODO: 드라이브가 여러개일 때 driveId 구할 수 있는 방법을 찾아야함
                }
            } else {
                dispatch(
                    updateFolderState({
                        [item.id]: { ...folder, isFold: true },
                    }),
                );
                abort(`folderChildren:${item.id}`);
            }

            //폴더 선택·목록 이동은 접힘 여부와 무관하게 수행한다
            if (!isClickArrow) {
                onClickTreeItem({
                    id: item.id,
                    driveId: item.driveId,
                    hierarchyType: GROUPING_VIEW_TYPE.FOLDER,
                });
                if (!checkUseHistory) dispatch(updateSelectedFolder(item));
            }
            Promise.resolve();
        } catch (error) {
            console.error(error);
            Promise.reject(error);
        }
    };

    const onClickCreateNewFolder = async (name, folder) => {
        try {
            const params = { name, parent: { id: folder.id } };
            const driveId = folder.driveId;

            await dispatch(createDriveFolder(driveId, params));
            const queryParameters = {
                parentId: folder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: project.id };
            await dispatch(
                getFoldersList({
                    driveId,
                    queryParameters,
                    headerParams,
                    signal: getNewController(`folderChildren:${folder.id}`),
                }),
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const onRenameFolder = async (item, name) => {
        try {
            const params = { name };

            const queryParameters = {
                parentId: item.parent,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: project.id };

            await dispatch(updateFolderInfo({ driveId: item.driveId, folderId: item.id }, params));
            await dispatch(
                getFoldersList({
                    driveId: item.driveId,
                    queryParameters,
                    headerParams,
                    signal: getNewController(`folderChildren:${item.parent}`),
                }),
            );
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::label::${"Error renaming from {{item1}} to {{item2}}."}`, {
                            item1: item.name,
                            item2: name,
                        }) +
                        "- " +
                        error?.data?.message,
                }),
            );

            return Promise.reject(error);
        }
    };

    useEffect(async () => {
        const folder = folderTreeView.folderList.selectedFolder?.folder;
        if (!domain) return null;
        if (!folder) {
            const rootFolder = await getVirtualFolder({});
            const { id, name, depth } = rootFolder?.folderPath || {};
            setCurrentFolderInfo({
                folderId: id,
                folderPath: [{ id, name, depth }],
            });
        } else {
            const folderPathList = await getVirtualFolderFullPathList({
                folderId: folder?.id,
            });

            setCurrentFolderInfo({
                folderId: folder?.id,
                folderPath: folderPathList
                    ? [...folderPathList, { id: folder?.id, name: folder?.name, depth: folderPathList.length }]
                    : [],
            });
        }
    }, [folderTreeView.folderList.selectedFolder?.folder]);

    //컴포넌트 언마운트 시 진행 중인 모든 요청 취소
    useEffect(() => {
        return () => {
            abortAll();
        };
    }, [abortAll]);

    return {
        onRenameFolder,
        onClickFolderTree,
        onClickCreateNewFolder,
        currentFolderInfo,
    };
};

export default useFolder;
